import { BitmapLayer, Layer } from 'deck.gl';
import { LayerManager } from '../LayerManager';

export interface BitmapLayerConfig {
  id: string;
  imageUrl: string;
  bounds: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
  elevation?: number;
  pickable?: boolean;
  visible?: boolean;
  opacity?: number;
}

export class BitmapLayerManager implements LayerManager {
  private config: BitmapLayerConfig;
  private layer: BitmapLayer | null = null;

  constructor(config: BitmapLayerConfig) {
    // Option 1: Use nullish coalescing to provide defaults for undefined values
    this.config = {
      id: config.id,
      imageUrl: config.imageUrl,
      bounds: config.bounds,
      elevation: config.elevation ?? 20, // default elevation
      pickable: config.pickable ?? true,
      visible: config.visible ?? true,
      opacity: config.opacity ?? 1.0
    };
    this.createLayer();
  }

  private createLayer(): void {
    const [minLon, minLat, maxLon, maxLat] = this.config.bounds;
    const elevation = this.config.elevation!;

    const layerProps = {
      id: this.config.id,
      bounds: [
        [minLon, minLat, elevation],
        [minLon, maxLat, elevation],
        [maxLon, maxLat, elevation],
        [maxLon, minLat, elevation]
      ] as [[number, number, number], [number, number, number], [number, number, number], [number, number, number]],
      image: this.config.imageUrl,
      pickable: this.config.pickable!,
      visible: this.config.visible!,
      opacity: this.config.opacity!
    };

    this.layer = new BitmapLayer(layerProps);
  }

  public getLayer(): Layer | null {
    return this.layer;
  }

  public updateConfig(newConfig: Partial<BitmapLayerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.createLayer();
  }

  public setVisibility(visible: boolean): void {
    this.config.visible = visible;
    if (this.layer) {
      this.layer = this.layer.clone({ visible });
    }
  }

  public setOpacity(opacity: number): void {
    this.config.opacity = Math.max(0, Math.min(1, opacity)); // Clamp between 0 and 1
    if (this.layer) {
      this.layer = this.layer.clone({ opacity: this.config.opacity });
    }
  }

  public setBounds(bounds: [number, number, number, number]): void {
    this.config.bounds = bounds;
    this.createLayer();
  }

  public setElevation(elevation: number): void {
    this.config.elevation = elevation;
    this.createLayer();
  }

  public getId(): string {
    return this.config.id;
  }

  public getConfig(): BitmapLayerConfig {
    return { ...this.config };
  }

  public getBounds(): [number, number, number, number] {
    return [...this.config.bounds];
  }

  public getElevation(): number {
    return this.config.elevation!;
  }

  public isVisible(): boolean {
    return this.config.visible!;
  }

  public getOpacity(): number {
    return this.config.opacity!;
  }

  /**
   * Creates a bitmap layer positioned around a center point with a given radius
   */
  public static createFromCenterAndRadius(config: {
    id: string;
    imageUrl: string;
    center: [number, number]; // [longitude, latitude]
    radius: number; // radius in degrees
    elevation?: number;
    pickable?: boolean;
    visible?: boolean;
    opacity?: number;
  }): BitmapLayerManager {
    const [centerLon, centerLat] = config.center;
    const radius = config.radius;
    
    const bounds: [number, number, number, number] = [
      centerLon - radius, // minLon
      centerLat - radius, // minLat
      centerLon + radius, // maxLon
      centerLat + radius  // maxLat
    ];

    return new BitmapLayerManager({
      id: config.id,
      imageUrl: config.imageUrl,
      bounds,
      elevation: config.elevation,
      pickable: config.pickable,
      visible: config.visible,
      opacity: config.opacity
    });
  }
}